import { usePageTitle } from "@/hooks/use-page-title";
import { ReportCard } from "@/components/report-card";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageAbout } from "@/components/page-about";

interface Report {
  id: string;
  title: string;
  description: string;
  category: "compliance" | "wallet" | "funding" | "partnership";
  lastGenerated?: string | null;
  frequency: "daily" | "weekly" | "monthly" | "quarterly" | "on-demand";
  dataAvailable: boolean;
}

async function downloadReport(reportId: string): Promise<void> {
  const response = await fetch(`/api/reports/${reportId}/download`, {
    credentials: "include",
  });
  
  if (!response.ok) {
    throw new Error("Failed to download report");
  }
  
  const blob = await response.blob();
  const contentDisposition = response.headers.get("Content-Disposition");
  let filename = `${reportId}-report.csv`;
  
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="(.+)"/);
    if (match) {
      filename = match[1];
    }
  }
  
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export default function Reports() {
  usePageTitle("Reports", "Generate and view financial and operational reports for the HealthWallet platform.");
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const { toast } = useToast();

  const { data: reports = [], isLoading } = useQuery<Report[]>({
    queryKey: ["/api/reports"],
  });

  const handleDownload = async (reportId: string, reportTitle: string) => {
    setDownloadingId(reportId);
    try {
      await downloadReport(reportId);
      toast({
        title: "Report Downloaded",
        description: `${reportTitle} has been downloaded successfully.`,
      });
    } catch (error) {
      toast({
        title: "Download Failed",
        description: "Failed to download report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const filteredReports = useMemo(() => {
    return reports.filter((report) => {
      const matchesSearch =
        searchQuery === "" ||
        report.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        report.description.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory =
        categoryFilter === "all" || report.category === categoryFilter;

      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, categoryFilter, reports]);

  return (
    <div className="space-y-6">
      <PageAbout
        title="Compliance Reports"
        description="Generate and download security-compliant documentation for regulatory audits, internal reviews, and stakeholder reporting. All reports follow healthcare industry standards."
        features={[
          "Pre-built report templates for wallet and funding compliance",
          "Wallet transaction summaries and fund allocation breakdowns",
          "Data access audit documentation",
          "Payment schedule compliance tracking and status reports",
          "Automated scheduling for daily, weekly, or monthly generation"
        ]}
        tips={[
          "Download reports before compliance audits for documentation",
          "Use the wallet transaction report to verify funding activities",
          "Schedule recurring reports to automate your compliance workflow"
        ]}
      />
      
      <div>
        <h1 className="text-3xl font-semibold">Compliance Reports</h1>
        <p className="text-muted-foreground mt-1">Generate and download compliance documentation</p>
      </div>

      <div className="flex gap-4 flex-wrap">
        <div className="relative flex-1 min-w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search reports..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="input-search"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-56" data-testid="select-category">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="compliance">Compliance</SelectItem>
            <SelectItem value="wallet">Wallet</SelectItem>
            <SelectItem value="funding">Funding</SelectItem>
            <SelectItem value="partnership">Partnership</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-48 w-full" data-testid={`skeleton-report-${i}`} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredReports.length === 0 ? (
            <div className="col-span-full text-center py-12 text-muted-foreground">
              No reports found matching your criteria
            </div>
          ) : (
            filteredReports.map((report) => (
            <ReportCard
              key={report.id}
              title={report.title}
              description={report.description}
              category={report.category}
              lastGenerated={report.lastGenerated ? new Date(report.lastGenerated) : undefined}
              frequency={report.frequency}
              onGenerate={() => handleDownload(report.id, report.title)}
              onDownload={report.dataAvailable ? () => handleDownload(report.id, report.title) : undefined}
              isDownloading={downloadingId === report.id}
            />
          ))
          )}
        </div>
      )}
    </div>
  );
}
